import React, { useEffect, useRef, useState } from "react";
import {
	object as yup_object,
	string as yup_string,
} from "yup";
import PublicAppVars from "utils/PublicAppVars.js";
import { useGlobalToastsContext } from "context/ToastProvider";
import Toast from "components/Toast";
import { TYPE_ERROR } from "components/Banner";
import CmsContentList from "components/data/CmsContentList";
import CmsContentRenderer from "components/data/CmsContentRenderer";

const cms = {
	header: 'miscText.fraud-detection-error-header',
	desc: 'miscText.fraud-detection-error-desc',
};

export const getTransUnionYupSchema = () => {
	if (!PublicAppVars.TRANS_UNION_BLACKBOX_ENABLED) {
		return yup_object().shape({});
	}

	const validations = {
		[ PublicAppVars.TRANS_UNION_BLACKBOX_ELEMENT_ID ]: yup_string().required(),
	};

	return yup_object().shape(validations);
};

// We'll use this when we want to validate the form but not run any of the additional logic of the useEffects
export const TransUnionValueOnly = (value = null) =>
	<input
		value={value}
		type="hidden"
		name={PublicAppVars.TRANS_UNION_BLACKBOX_ELEMENT_ID}
		id={PublicAppVars.TRANS_UNION_BLACKBOX_ELEMENT_ID}
	/>;

const TransUnion = ({ parentSetter = null }) => {
	const { toasts, setToast, removeToast } = useGlobalToastsContext();
	const inputRef = useRef();
	const [ localFingerPrint, setLocalFingerPrint ] = useState();

	// We'll use local state to determine if the toast has already been set,
	// otherwise we don't want to keep applying the toast if the user has closed it.
	const [ localToastWasSet, setLocalToastWasSet ] = useState(false);

	const hasACIFraudToast = toasts?.length
		? toasts.find(toast => toast.props?.list?.find(item => item === cms.header))
		: false;

	// Some flows we will want to get the fingerprint immediately so we don't get to the end of the flow and let the user know they need to turn adblocker off.
	// When that is the case we will call a parentSetter to return the fingerprint at the top level.
	useEffect(() => {
		if (inputRef.current?.value && !localFingerPrint) {
			parentSetter?.(inputRef.current?.value);
			setLocalFingerPrint(inputRef.current?.value);

			// Should there still be a race condition after TRANS_UNION_BLACKBOX_INPUT_TIMEOUT we'll manually close out the toast.
			removeToast();
		}
	}, [ inputRef.current?.value, localFingerPrint, parentSetter,removeToast ,hasACIFraudToast ]);

	useEffect(() => {
		// Give 2 seconds for the form to get the fingerprint
		const timeout = setTimeout(() => {

			// If ACI Fraud detection is enabled we need to check if the browser is potentially blocking the fingerprint from being placed in the form
			if (PublicAppVars.TRANS_UNION_BLACKBOX_ENABLED && !hasACIFraudToast && !localToastWasSet && !localFingerPrint) {
				setLocalToastWasSet(true);

				setToast(
					<CmsContentList list={Object.values(cms)}>{() =>
						<Toast
							type={TYPE_ERROR}
							title={
								<CmsContentRenderer
									contentKey={cms.header}
									fallbackValue="We’ve encountered an error"
								/>
							}
							text={
								<CmsContentRenderer
									contentKey={cms.desc}
									fallbackValue="An ad blocker may be interfering with our fraud detection system. Please try again with the ad blocker disabled."
								/>
							}
							onClosed={removeToast}
							timeout={0}
						/>
					}</CmsContentList>
				);
			}
		   }, PublicAppVars.TRANS_UNION_BLACKBOX_INPUT_TIMEOUT);
		return () => clearTimeout(timeout);

	}, [ localToastWasSet, setLocalToastWasSet, hasACIFraudToast, removeToast, setToast, localFingerPrint ]);

	// Bail out if black box is not enabled
	if (!PublicAppVars.TRANS_UNION_BLACKBOX_ENABLED) {
		return null;
	}

	return <>
		<input
			ref={inputRef}
			type="hidden"
			name={PublicAppVars.TRANS_UNION_BLACKBOX_ELEMENT_ID}
			id={PublicAppVars.TRANS_UNION_BLACKBOX_ELEMENT_ID}
		/>
	</>;
};

export default TransUnion;
